import React, { useState } from 'react'
import { Card, Col, Popconfirm, Switch, Button, Tag } from 'antd-v4'
import { BlazeButton } from '../../../components/CustomButtons/BlazeButton'
import { ILocation, IAdminExternalSource } from '@getgreenline/homi-shared'
import { THEME_COLORS } from '@getgreenline/shared'
import { ISubscriptionContract } from '@getgreenline/webhooks'
import { isSubscriptionActive } from './subscriptionHelpers'

interface Props {
  subscription: ISubscriptionContract
  locations: ILocation[]
  externalSources: IAdminExternalSource[]
  /** true while this specific subscription's delete request is in flight */
  isDeleting: boolean
  onDelete: (subscription: ISubscriptionContract) => Promise<void>
  onToggleActive: (subscription: ISubscriptionContract, isActive: boolean) => Promise<void>
  setIsEditing: (isEditing: boolean) => void
  setSubscriptionToEdit: (subscription: ISubscriptionContract) => void
}

export const SubscriptionCard = (props: Props) => {
  const { companyData } = props.subscription
  const { eventType, locationId, integratorId, url } = companyData
  const [isToggling, setIsToggling] = useState(false)

  const getExternalSourceName = (integratorId: string) => {
    const parsedIntegratorId = parseInt(integratorId)
    const source = props.externalSources.find((source) => source.id === parsedIntegratorId)
    return source?.name
  }

  const getLocationName = (locationId: number | undefined) => {
    const location = props.locations.find((location) => location.id === locationId)
    return location?.name
  }

  const handleToggleActive = async (nextIsActive: boolean) => {
    setIsToggling(true)
    try {
      await props.onToggleActive(props.subscription, nextIsActive)
    } finally {
      setIsToggling(false)
    }
  }

  const isActive = isSubscriptionActive(props.subscription)

  return (
    <Col xxl={6} xl={6} lg={8} md={12} sm={24} xs={24}>
      <Card
        title={
          <div className='d-flex align-items-center'>
            <span className='mr-2'>{eventType}</span>
            {!isActive && <Tag>Inactive</Tag>}
          </div>
        }
        extra={
          <Switch
            checked={isActive}
            loading={isToggling}
            disabled={props.isDeleting}
            onChange={handleToggleActive}
          />
        }
        className='mb-2'
      >
        <div className='mb-3'>
          <div className='d-flex justify-content-between'>
            <span className='text-muted'>Location</span>
            <span>{getLocationName(locationId) || '—'}</span>
          </div>
          <div className='d-flex justify-content-between'>
            <span className='text-muted'>URL</span>
            <span className='text-truncate ml-2' style={{ maxWidth: '65%' }} title={url}>
              {url}
            </span>
          </div>
          <div className='d-flex justify-content-between'>
            <span className='text-muted'>Integrator</span>
            <span>{getExternalSourceName(integratorId) || '—'}</span>
          </div>
        </div>
        <>
          <BlazeButton
            buttonProps={{
              disabled: isToggling || props.isDeleting,
              onClick: () => {
                props.setIsEditing(true)
                props.setSubscriptionToEdit(props.subscription)
              },
            }}
            titleProps={{
              title: 'Edit',
            }}
          />
          <Popconfirm
            title="Delete this webhook subscription? This can't be undone."
            okText='Delete'
            okType='danger'
            cancelText='Cancel'
            disabled={isToggling || props.isDeleting}
            onConfirm={() => props.onDelete(props.subscription)}
          >
            <Button
              type='link'
              loading={props.isDeleting}
              disabled={isToggling}
              className='ml-2'
              style={isToggling ? undefined : { color: THEME_COLORS.ACTIONS.danger }}
            >
              Delete
            </Button>
          </Popconfirm>
        </>
      </Card>
    </Col>
  )
}
